import { Injectable } from "@angular/core";
import * as errorConfig from "src/app/config/errors";
import * as fromUI from "../actions/ui.actions";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, mergeMap } from "rxjs/operators";
import { StudentShortTextAnswer } from "src/app/shared/interfaces/student-short-text-answer";
import * as fromShortTextExercise from "../actions/exercise-short-text.actions";
import { courseProgressLoad } from "../actions/course-progress.actions";
import { HttpErrorResponse } from "@angular/common/http";
import { of } from "rxjs";
import { WarningModalService } from "src/app/shared/services/warning-modal/warning-modal.service";
import { ExerciseShortTextApiService } from "src/app/shared/services/exercise/exercise-short-text-api.service";
import { GradeTextAnswer } from "src/app/shared/interfaces/grade-text-answer";

@Injectable()
export class ExerciseShortTextEffect {
  commentStudentAnswer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromShortTextExercise.COMMENT_STUDENT_ANSWER),
      mergeMap((action: fromShortTextExercise.CommentStudentAnswer) =>
        this.shortTextService.commentStudentAnswer(action.payload).pipe(
          map((result: GradeTextAnswer) => {
            return new fromShortTextExercise.CommentStudentAnswerSuccess({
              exerciseId: action.payload.exerciseId,
              gradeTextAnswer: result,
            });
          }),
        ),
      ),
    ),
  );

  gradeStudentAnswer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromShortTextExercise.GRADE_STUDENT_ANSWER),
      mergeMap((action: fromShortTextExercise.GradeStudentAnswer) =>
        this.shortTextService.reviewStudentAnswer(action.payload).pipe(
          map((result: GradeTextAnswer) => {
            return new fromShortTextExercise.GradeStudentAnswerSuccess({
              exerciseId: action.payload.exerciseId,
              gradeTextAnswer: result,
            });
          }),
          catchError(() =>
            of(new fromShortTextExercise.CommentStudentAnswerFailure()),
          ),
        ),
      ),
    ),
  );

  gradeStudentAnswerManual$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromShortTextExercise.GRADE_STUDENT_ANSWER_MANUAL),
      mergeMap((action: fromShortTextExercise.GradeStudentAnswerManual) =>
        this.shortTextService.manualGradeStudentAnswer(action.payload).pipe(
          map((result: GradeTextAnswer) => {
            return new fromShortTextExercise.GradeStudentAnswerSuccess({
              exerciseId: action.payload.exerciseId,
              gradeTextAnswer: result,
            });
          }),
          catchError(() =>
            of(new fromShortTextExercise.GradeStudentAnswerManualFailure()),
          ),
        ),
      ),
    ),
  );

  listTeacherGradeAnswers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromShortTextExercise.LIST_TEACHER_GRADE_ANSWERS),
      mergeMap((action: fromShortTextExercise.ListTeacherGradeAnswers) =>
        this.shortTextService.teacherListStudentAnswers(action.payload).pipe(
          map((result: GradeTextAnswer[]) => {
            return new fromShortTextExercise.ListTeacherGradeAnswersSuccess({
              gradeAnswers: result,
              exerciseId: action.payload.exerciseId,
            });
          }),
          catchError(() =>
            of(new fromShortTextExercise.ListTeacherGradeAnswersFailure()),
          ),
        ),
      ),
    ),
  );

  listStudentAnswers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromShortTextExercise.LIST_STUDENT_ANSWERS),
      mergeMap((action: fromShortTextExercise.ListStudentAnswers) =>
        this.shortTextService.studentListAnswers(action.payload).pipe(
          map((result: StudentShortTextAnswer[]) => {
            return new fromShortTextExercise.ListStudentAnswersSuccess({
              exerciseId: action.payload.exerciseId,
              answers: result,
            });
          }),
          catchError(() =>
            of(new fromShortTextExercise.ListStudentAnswersFailure()),
          ),
        ),
      ),
    ),
  );

  answerShortText$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromShortTextExercise.ANSWER_TEXT),
      mergeMap((action: fromShortTextExercise.AnswerText) =>
        this.shortTextService.answerTextExercise(action.payload).pipe(
          mergeMap((result: StudentShortTextAnswer) => {
            const actions = [
              {
                type: fromShortTextExercise.ANSWER_TEXT_SUCCESS,
                payload: {
                  studentShortTextAnswer: result,
                  exerciseId: action.payload.ExerciseId,
                },
              },
              {
                type: fromUI.RESET_RUNNING_EXERCISE,
              },
            ];

            if (action.payload.CourseId && action.payload.GroupId) {
              actions.push(
                courseProgressLoad({
                  CourseId: action.payload.CourseId,
                  GroupId: action.payload.GroupId,
                }),
              );
            }

            return actions;
          }),
          catchError((errorResponse: HttpErrorResponse) => {
            if (errorResponse.error.answer) {
              this.warningModalService.showModal({
                modalTitle: `GLOBAL.MESSAGES.ERROR_TITLES.ERROR_${errorResponse.status}`,
                text: errorResponse.error.answer[0],
                icon: errorConfig.ERROR_ICONS[errorResponse.status],
              });
            }

            return of(
              new fromShortTextExercise.AnswerTextFailure({
                exercise: { id: action.payload.ExerciseId },
              }),
            );
          }),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private shortTextService: ExerciseShortTextApiService,
    private warningModalService: WarningModalService,
  ) {}
}
